import React, { useState, useEffect } from "react";
import { Row, Col, Badge } from "react-bootstrap";
import {
  CommonModal,
  CommonButton,
  CommonStepIndicator,
  CommonInput,
} from "../Input";
import DataTable from "../table/DataTable";
import Swal from "sweetalert2";

// Import bill images for denominations
import bill100 from "../../../images/cash/100_dollar.png";
import bill50 from "../../../images/cash/50_dollar.png";
import bill20 from "../../../images/cash/20_dollar.png";
import bill10 from "../../../images/cash/10_dollar.png";
import bill5 from "../../../images/cash/5_dollar.png";
import bill1 from "../../../images/cash/1_dollar.png";
import coin1 from "../../../images/cash/1_dollar_bronze.png";
import coin50c from "../../../images/cash/50_cents.png";
import coin25c from "../../../images/cash/25_cents.png";
import coin10c from "../../../images/cash/10_cents.png";
import coin5c from "../../../images/cash/5_cents.png";
import coin1c from "../../../images/cash/1_cent.png";

const RefundInformationModal = ({ show, onHide, refund }) => {
  const [currentStep, setCurrentStep] = useState(0); // 0: Details, 1: Type, 2: Summary, 3: Success
  const [refundType, setRefundType] = useState("with_charge");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [customAmount, setCustomAmount] = useState("");
  const [outQuantities, setOutQuantities] = useState({});

  useEffect(() => {
    if (!show) {
      setCurrentStep(0);
      setRefundType("with_charge");
      setRefundMethod("cash");
      setCustomAmount("");
      setOutQuantities({});
    }
  }, [show]);

  if (!refund) return null;

  const steps = ["Refund Type", "Refund Summary", "Refund Success"];

  const denominations = [
    {
      id: "bill100",
      label: "100 Dollars",
      value: 100,
      image: bill100,
      inventory: 3,
    },
    {
      id: "bill50",
      label: "50 Dollars",
      value: 50,
      image: bill50,
      inventory: 2,
    },
    {
      id: "bill20",
      label: "20 Dollars",
      value: 20,
      image: bill20,
      inventory: 3,
    },
    {
      id: "bill10",
      label: "10 Dollars",
      value: 10,
      image: bill10,
      inventory: 2,
    },
    { id: "bill5", label: "5 Dollars", value: 5, image: bill5, inventory: 3 },
    { id: "bill1", label: "1 Dollar", value: 1, image: bill1, inventory: 11 },
    { id: "coin1", label: "1 Dollar", value: 1, image: coin1, inventory: 2 },
    {
      id: "coin50c",
      label: "50 Cents",
      value: 0.5,
      image: coin50c,
      inventory: 1,
    },
    {
      id: "coin25c",
      label: "25 Cents",
      value: 0.25,
      image: coin25c,
      inventory: 0,
    },
    {
      id: "coin10c",
      label: "10 Cents",
      value: 0.1,
      image: coin10c,
      inventory: 1,
    },
    {
      id: "coin5c",
      label: "5 Cents",
      value: 0.05,
      image: coin5c,
      inventory: 4,
    },
  ];

  const handleOutChange = (id, val) =>
    setOutQuantities({ ...outQuantities, [id]: parseInt(val) || 0 });

  const totalRefundAmount =
    refundType === "specific"
      ? parseFloat(customAmount) || 0
      : refundType === "without_charge"
        ? parseFloat(refund.sendAmount) || 0
        : (parseFloat(refund.sendAmount) || 0) +
          (parseFloat(refund.serviceCharge) || 0);

  const totalRefundCash = Object.keys(outQuantities).reduce((total, id) => {
    const denom = denominations.find((d) => d.id === id);
    return total + (denom ? denom.value * outQuantities[id] : 0);
  }, 0);

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      title={currentStep === 3 ? "" : "Refund Information"}
      size="xl"
      headerClassName={currentStep === 3 ? "" : "border-bottom pb-3"}
      footer={
        <div className="d-flex justify-content-end gap-3 w-100 px-4 py-2 bg-white rounded-bottom">
          {currentStep === 0 && (
            <CommonButton className="px-5" onClick={() => setCurrentStep(1)}>
              Refund
            </CommonButton>
          )}
          {currentStep === 1 && (
            <CommonButton className="px-5" onClick={() => setCurrentStep(2)}>
              Next
            </CommonButton>
          )}
          {currentStep === 2 && (
            <>
              <CommonButton
                variant="outline"
                className="px-5"
                onClick={() => setCurrentStep(1)}
              >
                Back
              </CommonButton>
              <CommonButton className="px-5" onClick={() => setCurrentStep(3)}>
                Confirm
              </CommonButton>
            </>
          )}
        </div>
      }
    >
      <div className="refund-modal-content">
        {currentStep > 0 && (
          <div className="mb-5 mt-2">
            <CommonStepIndicator steps={steps} currentStep={currentStep} />
          </div>
        )}

        {currentStep === 0 && (
          <div>
            {/* Existing Sender Section */}
            <div className="fw-bold fs-13 mb-1 text-black">Sender</div>
            <div
              className="p-4 mb-4 border border-light"
              style={{ borderRadius: "12px", backgroundColor: "#FAFAFA" }}
            >
              <Row className="gy-4">
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">Customer Type</div>
                  <div className="fw-medium text-black fs-13">
                    {refund.senderType || "Organization"}
                  </div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">Sender Name</div>
                  <div className="fw-medium text-black fs-13">
                    {refund.senderName || "-"}
                  </div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">Customer ID</div>
                  <div className="fw-medium text-black fs-13">481</div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">ID Type</div>
                  <div className="fw-medium text-black fs-13">
                    NRIC (Citizen)
                  </div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">ID Number</div>
                  <div className="fw-medium text-black fs-13">811111111999</div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">Payment Method</div>
                  <div className="fw-medium text-black fs-13">
                    {refund.paymentMethod || "Cash"}
                  </div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">
                    Bank Transfer Amount
                  </div>
                  <div className="fw-medium text-black fs-13">
                    {refund.sendAmount || "500 USD"}
                  </div>
                </Col>
                <Col sm={3} md={2}>
                  <div className="text-muted fs-12 mb-1">Cash Amount</div>
                  <div className="fw-medium text-black fs-13">1.2 USD</div>
                </Col>
              </Row>
            </div>

            {/* Existing Beneficiaries Section */}
            <div className="fw-bold fs-13 mb-1 text-black">Beneficiaries</div>
            <div
              className="p-4 border border-light"
              style={{ borderRadius: "12px", backgroundColor: "#FAFAFA" }}
            >
              <Row className="gy-4">
                <Col lg={8}>
                  <div className="mb-4">
                    <Badge
                      bg="light"
                      className="px-3 py-1 border rounded"
                      style={{
                        fontSize: "11px",
                        color: "#6941C6",
                        backgroundColor: "#F4F3FF",
                        borderColor: "#D6BBFB",
                      }}
                    >
                      Refunding
                    </Badge>
                  </div>
                  <Row className="gy-4">
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">
                        Transaction ID
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="fw-medium text-black fs-13 me-2">
                          {refund.transactionId || "-"}
                        </span>
                        <i
                          className="far fa-copy text-muted cursor-pointer"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">
                        Created Date & Time
                      </div>
                      <div className="fw-medium text-black fs-13">
                        {refund.displayDate} {refund.time}
                      </div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">Receiver Name</div>
                      <div className="fw-medium text-black fs-13">
                        Thị Hạnh Nguyễn Kiều
                      </div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">Receiver Type</div>
                      <div className="fw-medium text-black fs-13">
                        Individual
                      </div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">
                        Receiving Method
                      </div>
                      <div className="fw-medium text-black fs-13">
                        {refund.receivingMethod || "-"}
                      </div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted fs-12 mb-1">Bank/Agent</div>
                      <div className="fw-medium text-black fs-13">
                        ASIA UNITED BANK
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col lg={4}>
                  <div
                    className="border border-1 p-4 h-100 bg-white"
                    style={{ borderRadius: "12px" }}
                  >
                    <div className="d-flex justify-content-between mb-3">
                      <span className="text-muted fs-12">Receiving Amount</span>
                      <span className="fw-medium text-black fs-13">
                        26,176.5 PHP
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <span className="text-muted fs-12">Send Amount</span>
                      <span className="fw-medium text-black fs-13">
                        500 USD
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <span className="text-muted fs-12">Service Charge</span>
                      <span className="fw-medium text-black fs-13">
                        1.2 USD
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mt-2 pt-3 border-top">
                      <span className="text-muted fs-12">Total Amount</span>
                      <span className="fw-bold text-black fs-14">
                        501.2 USD
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div className="px-2">
            <h5 className="fw-bold text-black mb-3">
              <i className="far fa-file-alt me-2 text-muted"></i>Refund type
            </h5>
            <div className="refund-types mb-4">
              <div
                className={`p-3 border mb-3 cursor-pointer d-flex align-items-center ${
                  refundType === "with_charge"
                    ? "border-primary bg-light-blue"
                    : "border-light"
                }`}
                style={{ borderRadius: "12px" }}
                onClick={() => {
                  setRefundType("with_charge");
                  setCustomAmount("");
                }}
              >
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    checked={refundType === "with_charge"}
                    readOnly
                  />
                </div>
                <div>
                  <div className="fw-bold text-black fs-13">
                    Refund with Service Charge
                  </div>
                  <div className="text-muted fs-11">
                    Include service charge in refund
                  </div>
                </div>
              </div>

              <div
                className={`p-3 border mb-3 cursor-pointer d-flex align-items-center ${
                  refundType === "without_charge"
                    ? "border-primary bg-light-blue"
                    : "border-light"
                }`}
                style={{ borderRadius: "12px" }}
                onClick={() => {
                  setRefundType("without_charge");
                  setCustomAmount("");
                }}
              >
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    checked={refundType === "without_charge"}
                    readOnly
                  />
                </div>
                <div>
                  <div className="fw-bold text-black fs-13">
                    Refund without Service Charge
                  </div>
                  <div className="text-muted fs-11">
                    Exclude service charge from refund
                  </div>
                </div>
              </div>

              <div
                className={`p-3 border mb-3 cursor-pointer d-flex align-items-center ${
                  refundType === "specific"
                    ? "border-primary bg-light-blue"
                    : "border-light"
                }`}
                style={{ borderRadius: "12px" }}
                onClick={() => setRefundType("specific")}
              >
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    checked={refundType === "specific"}
                    readOnly
                  />
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold text-black fs-13">
                    Refund Specific Amount
                  </div>
                  <div className="text-muted fs-11">
                    Enter custom refund amount
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="fw-bold text-black fs-13 mb-2">
                Refund amount
              </label>
              <div className="position-relative d-flex align-items-center">
                <span
                  className="position-absolute ps-3 text-muted fw-bold fs-14"
                  style={{ left: 0 }}
                >
                  USD
                </span>
                <input
                  type="text"
                  className="form-control ps-5 fw-bold text-black fs-18 py-3"
                  style={{ borderRadius: "10px" }}
                  value={
                    refundType === "specific" ? customAmount : totalRefundAmount
                  }
                  onChange={(e) => setCustomAmount(e.target.value)}
                  readOnly={refundType !== "specific"}
                />
              </div>
            </div>

            <h5 className="fw-bold text-black mb-3">Select method</h5>
            <Row className="g-3 mb-4">
              {[
                {
                  id: "cash",
                  label: "Cash",
                  sub: "Refund in cash",
                  icon: "fa-money-bill-wave",
                },
                {
                  id: "bank_transfer",
                  label: "Bank transfer",
                  sub: "Transfer to bank account",
                  icon: "fa-university",
                },
                {
                  id: "ewallet",
                  label: "Ewallet",
                  sub: "Refund to digital wallet",
                  icon: "fa-wallet",
                },
              ].map((method) => (
                <Col key={method.id} md={4}>
                  <div
                    className={`p-4 border text-center cursor-pointer h-100 transition-all d-flex flex-column align-items-center justify-content-center ${
                      refundMethod === method.id
                        ? "border-primary bg-light-blue shadow-sm"
                        : "border-light"
                    }`}
                    onClick={() => setRefundMethod(method.id)}
                    style={{ borderRadius: "12px" }}
                  >
                    <div
                      className={`mb-3 rounded-circle d-flex align-items-center justify-content-center ${
                        refundMethod === method.id
                          ? "bg-primary text-white"
                          : "bg-light text-muted"
                      }`}
                      style={{ width: "50px", height: "50px" }}
                    >
                      <i className={`fas ${method.icon} fs-20`}></i>
                    </div>
                    <div className="fw-bold text-black fs-14 mb-1">
                      {method.label}
                    </div>
                    <div className="text-muted fs-11">{method.sub}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {currentStep === 2 && (
          <div className="px-2">
            <div
              className="border border-light p-3 px-4 rounded-4 mb-4"
              style={{ borderRadius: "12px", backgroundColor: "#FAFAFA" }}
            >
              <Row className="align-items-center">
                <Col md={3} className="border-end">
                  <div className="text-muted fs-11 text-uppercase fw-bold mb-1">
                    Send Amount
                  </div>
                  <div className="fw-bold text-black fs-14">
                    {refund.sendAmount || "500 USD"}
                  </div>
                </Col>
                <Col md={3} className="border-end px-4">
                  <div className="text-muted fs-11 text-uppercase fw-bold mb-1">
                    Service Charge
                  </div>
                  <div className="fw-bold text-black fs-14">
                    {refund.serviceCharge || "1.2 USD"}
                  </div>
                </Col>
                <Col md={3} className="border-end px-4">
                  <div className="text-muted fs-11 text-uppercase fw-bold mb-1">
                    Total Paid
                  </div>
                  <div className="fw-bold text-black fs-14">
                    {refund.totalAmount || "501.2 USD"}
                  </div>
                </Col>
                <Col md={3} className="px-4">
                  <div className="text-muted fs-11 text-uppercase fw-bold mb-1">
                    Method
                  </div>
                  <div className="fw-bold text-black fs-14 text-capitalize">
                    {refundMethod.replace("_", " ")}
                  </div>
                </Col>
              </Row>
            </div>

            <div className="mb-4 pt-2">
              <div className="text-muted fs-12 fw-bold mb-1">Refund amount</div>
              <div className="fw-bold text-primary fs-20">
                {totalRefundAmount.toFixed(2)} USD
              </div>
            </div>

            {refundMethod === "cash" ? (
              <div className="cash-denominations mt-3">
                <div className="d-flex justify-content-end align-items-center gap-3 mb-3">
                  <Badge
                    bg="light"
                    className="text-black fw-bold px-3 py-2 border"
                  >
                    Total cash:{" "}
                    <span className="text-primary">
                      USD {totalRefundCash.toFixed(2)}
                    </span>
                  </Badge>
                  <Badge
                    bg="light"
                    className="text-black fw-bold px-3 py-2 border"
                  >
                    Total refund:{" "}
                    <span className="text-primary">
                      USD {totalRefundAmount.toFixed(2)}
                    </span>
                  </Badge>
                </div>

                <div className="card shadow-none border border-light rounded-4 overflow-hidden">
                  <DataTable
                    columns={[
                      {
                        label: "IMAGE",
                        key: "image",
                        render: (item) => (
                          <div
                            className="d-flex align-items-center justify-content-center p-1 border rounded bg-white"
                            style={{ width: "45px", height: "30px" }}
                          >
                            <img
                              src={item.image}
                              alt=""
                              style={{ width: "100%", height: "auto" }}
                            />
                          </div>
                        ),
                      },
                      {
                        label: "DENOMINATION",
                        key: "label",
                        render: (item) => (
                          <span className="fw-bold text-black fs-12">
                            {item.label}
                          </span>
                        ),
                      },
                      {
                        label: "INVENTORY",
                        key: "inventory",
                        className: "text-center",
                        render: (item) => (
                          <span className="text-muted fs-12 d-flex align-items-center justify-content-center gap-2">
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor:
                                  item.inventory > 0 ? "#10B981" : "#EF4444",
                              }}
                            ></span>
                            {item.inventory}
                          </span>
                        ),
                      },
                      {
                        label: "QUANTITY",
                        key: "quantity",
                        className: "text-center",
                        render: (item) => (
                          <input
                            type="number"
                            className="form-control text-center mx-auto"
                            style={{
                              height: "32px",
                              width: "70px",
                              fontSize: "13px",
                            }}
                            value={outQuantities[item.id] || 0}
                            onChange={(e) =>
                              handleOutChange(item.id, e.target.value)
                            }
                          />
                        ),
                      },
                      {
                        label: "TOTAL",
                        key: "total",
                        className: "text-end pe-4",
                        render: (item) => (
                          <span className="fw-bold text-black fs-13">
                            USD{" "}
                            {(
                              item.value * (outQuantities[item.id] || 0)
                            ).toFixed(2)}
                          </span>
                        ),
                      },
                    ]}
                    data={denominations}
                    showPagination={false}
                    minWidth="100%"
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}

        {currentStep === 3 && (
          <div
            className="d-flex align-items-center justify-content-center py-5"
            style={{ minHeight: "60vh" }}
          >
            <div
              className="text-center p-5 rounded-4"
              style={{ maxWidth: "600px" }}
            >
              <div className="mb-4">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M30 0C13.4315 0 0 13.4315 0 30C0 46.5685 13.4315 60 30 60C46.5585 60 60 46.5685 60 30C60 13.4315 46.5585 0 30 0ZM24 45L9 30L13.242 25.758L24 36.5145L46.758 13.758L51 18L24 45Z"
                    fill="#10B981"
                  />
                </svg>
              </div>
              <h3 className="fw-bold text-success mb-3 fs-24">
                Refunded Successfully!
              </h3>
              <p className="text-muted mb-5 fs-14">
                The refund has been processed. You can now download, print, or
                email the receipt for this transaction.
              </p>
              <CommonButton className="px-5 w-100" onClick={onHide}>
                Close
              </CommonButton>
            </div>
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default RefundInformationModal;
